import React, { useState, useCallback, useEffect } from "react"
import { LayoutChangeEvent, Platform, ViewStyle } from "react-native"
import { Gesture, GestureDetector } from "react-native-gesture-handler"
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  runOnJS,
  clamp,
  SharedValue,
  useDerivedValue,
} from "react-native-reanimated"

import styled from "styled-components/native"

import { useWillUnmount } from "@treefort/lib/use-will-unmount"

import { useProgress, useSeekable } from "../hooks/audio"
import { audioPlayer, Event } from "../lib/audio-player"
import Time from "./time"
import { useTokens } from "./tokens-provider"

// HACK: Applying `transform: translateZ(0);` fixes a known bug with Safari
// where `overflow: hidden;` doesn't work properly when both border radii and
// transforms are at play. See:
// https://gist.github.com/domske/b66047671c780a238b51c51ffde8d3a0
const trackBackgroundSafariStyleHack =
  Platform.OS === "web"
    ? ({ transform: [{ translateZ: 0 }] } as unknown as ViewStyle)
    : undefined

export const ProgressContainer = styled.View<{ $width?: number }>`
  position: relative;
  flex-shrink: 0;
  flex-direction: column;
  width: ${(props) => (props.$width ? props.$width + "px" : "100%")};
  padding-top: ${({ theme }) => theme.spacing.tiny}px;
`

const Track = styled.View<{
  $height: number
  $labelPositionYAdjustment: number
}>`
  margin-bottom: -${({ $labelPositionYAdjustment }) => $labelPositionYAdjustment}px;
  flex-direction: row;
  align-items: center;
  height: ${({ $height }) => $height}px;
`

const TrackBackground = styled.View`
  width: 100%;
  height: ${({ theme }) => theme.audioPlayerProgress.trackHeight}px;
  border-radius: ${({ theme }) => theme.audioPlayerProgress.trackHeight / 2}px;
  background-color: ${({ theme }) =>
    theme.audioPlayerProgress.trackBackgroundColor};
  overflow: hidden;
`

const TrackProgress = styled(Animated.View)`
  position: absolute;
  right: 100%;
  width: 100%;
  height: ${({ theme }) => theme.audioPlayerProgress.trackHeight}px;
  border-radius: ${({ theme }) => theme.audioPlayerProgress.trackHeight / 2}px;
  background-color: ${({ theme }) => theme.audioPlayerProgress.color};
`

const HandleBackground = styled(Animated.View)<{ $show: boolean }>`
  position: absolute;
  background-color: ${({ theme }) => theme.colors.background.primary};
  width: ${({ theme }) => theme.audioPlayerProgress.handleRadius * 2}px;
  height: ${({ theme }) => theme.audioPlayerProgress.handleRadius * 2}px;
  border-radius: ${({ theme }) => theme.audioPlayerProgress.handleRadius}px;
  opacity: ${(props) => (props.$show ? 1 : 0)};
  align-items: center;
  justify-content: center;
`

const Handle = styled.View`
  background-color: ${({ theme }) => theme.audioPlayerProgress.handleColor};
  width: ${({ theme }) => theme.audioPlayerProgress.handleRadius * 2 - 4}px;
  height: ${({ theme }) => theme.audioPlayerProgress.handleRadius * 2 - 4}px;
  border-radius: ${({ theme }) => theme.audioPlayerProgress.handleRadius}px;
`

const TimeLabelContainer = styled.View<{ $labelPositionYAdjustment: number }>`
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  margin-bottom: ${({ $labelPositionYAdjustment }) =>
    $labelPositionYAdjustment}px;
`

/**
 * Render the start/end time labels. These labels live in their own component
 * because they re-render a *lot* (every second + continuously if the user is
 * seeking).
 */
function TimeLabels({
  duration,
  width,
  positionX,
  positionYAdjustment,
}: {
  duration: number
  width: number
  positionX: SharedValue<number>
  positionYAdjustment: number
}) {
  const calculatePosition = useCallback(
    (positionX: number) => {
      return duration * (positionX / width)
    },
    [duration, width],
  )

  // NOTE: The display times for the progress bar are calculated from the
  // animated position of the progress bar (instead of values from the audio
  // player) so that they update in realtime when the user is seeking.
  const [position, setPosition] = useState(0)
  const updatePosition = useCallback(
    (positionX: number) => setPosition(calculatePosition(positionX)),
    [calculatePosition],
  )

  useDerivedValue(() => {
    runOnJS(updatePosition)(positionX.value)
  })

  return (
    <TimeLabelContainer
      pointerEvents="none"
      $labelPositionYAdjustment={positionYAdjustment}
    >
      <Time textStyle="caption">{duration > 0 ? position : undefined}</Time>
      <Time textStyle="caption" countdown={true}>
        {duration > position ? duration - position : undefined}
      </Time>
    </TimeLabelContainer>
  )
}

export default function AudioPlayerProgress(props: {
  compact?: boolean
  width?: number
}): React.JSX.Element {
  const { tokens } = useTokens()
  const [isSeeking, setIsSeeking] = useState(false)
  const [width, setWidth] = useState<number>(props.width || 0)
  const { position = 0, duration = 0 } = useProgress()
  const seekable = useSeekable()
  const willUnmount = useWillUnmount()

  // The audio player may technically be seekable without a set duration, but we
  // can only show a seekable UI if we have a duration.
  const showSeekable = seekable && duration > 0

  const currentX = (duration > 0 ? position / duration : 0) * width

  const positionX = useSharedValue(currentX)

  const onPanEnd = useCallback(
    (finalPositionX: number) => {
      audioPlayer
        .once([Event.Seeked, Event.SeekCancelled])
        .then(() => setIsSeeking(false))
      audioPlayer.skipToPosition(duration * (finalPositionX / width))
    },
    [duration, width],
  )

  const panGesture = Gesture.Pan()
    .onStart(() => {
      runOnJS(setIsSeeking)(true)
    })
    .onUpdate((event) => {
      positionX.value = clamp(event.x, 0, width)
    })
    .onEnd((event) => {
      runOnJS(onPanEnd)(event.x)
    })

  const handleRadius = tokens.audioPlayerProgress.handleRadius
  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: positionX.value - handleRadius }],
  }))

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      if (!props.width && !willUnmount.current) {
        setWidth(event.nativeEvent.layout.width)
      }
    },
    [props.width, willUnmount],
  )

  // When we're not panning, make the seek variables track the currentX prop.
  useEffect(
    () => {
      if (!isSeeking) {
        positionX.set(currentX)
      }
    },
    // We only want to force an update to the animation when we get a new currentX
    // value (indicating that the current position has changed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentX],
  )

  const labelPositionYAdjustment = props.compact ? 0 : 16

  return (
    <GestureDetector gesture={panGesture}>
      <ProgressContainer onLayout={onLayout} $width={props.width}>
        <Track
          $height={props.compact ? tokens.spacing.medium : tokens.minTapTarget}
          $labelPositionYAdjustment={labelPositionYAdjustment}
        >
          <TrackBackground style={trackBackgroundSafariStyleHack}>
            <TrackProgress style={animatedStyle} />
          </TrackBackground>
          <HandleBackground style={animatedStyle} $show={showSeekable}>
            <Handle />
          </HandleBackground>
        </Track>
        <TimeLabels
          duration={duration}
          width={width}
          positionX={positionX}
          positionYAdjustment={labelPositionYAdjustment}
        />
      </ProgressContainer>
    </GestureDetector>
  )
}
